import { Fragment } from "react"
import Link from "next/link"
import { Container, Row, Col, Breadcrumb } from "react-bootstrap"
import { useRouter } from "next/router"
import ReactPlayer from "react-player"
import styled from "styled-components"
import Image from "next/image"
import { StyledButton } from "@modules/Common/styled"
import { PublicActivityQuery } from "@generated/graphql"
import { isEmptyString } from "@utils/stringUtils"

const StyledBadgeSpan = styled.span`
  font-weight: 600;
  background-color: var(--bs-secondary);
  border-radius: 8px;
  margin: 4px;
  padding-left: 10px;
  padding-right: 10px;
  font-size: 1.2em;
`

const StyledSkillNameSpan = styled.div`
  text-align: center;
  display: inline-block;
  font-weight: 600;
  background:url(/assets/svg/ActivityDetailSkillName.svg);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  color: white;
  border-radius: 8px;
  margin: 5px;
  padding-left: 10px;
  padding-right: 10px;
  font-size: 1.3em;
`

const StyledBox = styled.div`
  position: absolute;
  text-align: left;
  font-weight: bolder;
  font-size: 1.5em;
  @media (max-width: 601px) {
    font-size: 1.1em;
    & > img {
      max-width: 320px;
    }
  }
`

const StyledOverlay = styled.div`
  border-radius: 16px;
  margin-top: 24px;
  margin-left: 16px;
  background: #EAEDEC;
  padding: 48px 48px 36px 48px;
  text-align: left;
  @media (max-width: 601px) {
    padding: 24px 24px 18px 24px;
  }
`

const StyledBoxHeader = styled.div`
  position: absolute;
  top: 5px;
  left: 42px;
  color: white;
  text-transform: uppercase;
  @media (max-width: 601px) {
    top: 8px;
  }
`

const StyledActivityHeader = styled.div`
  @media (max-width: 601px) {
    margin: 16px 24px;
  }
`

const StyledTag = styled(Link)`
  text-decoration: none;
  border-color: var(--bs-secondary);
  border-width: 2px;
  border-style: solid;
  border-radius: 12px;
  padding: 4px;
  margin-left: 4px;
`

interface IPublicActivity {
  activity: PublicActivityQuery["publicActivity"]
}

export default function PublicActivityView({ activity }: IPublicActivity) {

  const router = useRouter()
  return (
    <Container fluid className="h-100">
      {activity ? <>
        <Row>
          <Col xs={{ offset: 1 }} md={{ offset: 2 }}>
            <Breadcrumb>
              <Breadcrumb.Item linkAs={Link} href="/dashboard">Home</Breadcrumb.Item>
              <Breadcrumb.Item linkAs={Link} href="/skills">Skills</Breadcrumb.Item>
              <Breadcrumb.Item linkAs={Link} href={`/skills/${activity.skill?.slug}`}>{activity.skill?.name}</Breadcrumb.Item>
              <Breadcrumb.Item linkAs={Link} href={`/skills/${activity.slug}`} active>{activity.title}</Breadcrumb.Item>
            </Breadcrumb>
          </Col>
        </Row>
        <Row>
          <Col lg={{ span: 3, offset: 2 }} md={{ span: 6, offset: 2 }} xs={{ span: 8, offset: 1 }} style={{ textAlign: "right" }}>
            <img src={activity?.image || activity?.featuredImage || "/assets/img/logo.png"} style={{ maxWidth: "240px" }} />
          </Col>
          <Col lg={{ span: 4 }} style={{ textAlign: "left", position: "relative" }}>
            <StyledActivityHeader>
              <h1>{activity?.title}</h1>
              {!isEmptyString(activity?.shortDescription) &&
                <div dangerouslySetInnerHTML={{ __html: activity?.shortDescription || "" }} />}
              <div style={{ marginTop: "20px" }}>
                <StyledBadgeSpan>+{activity?.xps}XPs</StyledBadgeSpan>
                <StyledBadgeSpan>{activity?.difficultyLevel}</StyledBadgeSpan>
                <StyledBadgeSpan><img src="/assets/svg/ActivityDetailMinTag.svg" width="16px" />{activity?.timeRequired}</StyledBadgeSpan>
              </div>
              <br />
              <div>Tags: {activity.tags?.map((tag, index) => (<Fragment key={tag.id}>{(index ? " , " : "")}<Link href={`/tags/${tag.slug}`}>{tag.name}</Link></Fragment>))}</div>
            </StyledActivityHeader>
          </Col>
        </Row>
        <Row>
          <Col lg={{ span: 8, offset: 2 }} xs={{ span: 12, offset: 0 }} className="bg-white mt-3 rounded-lg">
            <StyledBox>
              <img src="/assets/svg/ActivityDetailChallenge.svg" />
              <StyledBoxHeader>Challenge</StyledBoxHeader>
            </StyledBox>
            <StyledOverlay>
              {activity?.challengeStatement && activity?.challengeStatement !== "<p><br></p>" &&
                <div dangerouslySetInnerHTML={{ __html: activity?.challengeStatement }} />}
              {activity?.materialsNeeded && activity?.materialsNeeded !== "<p><br></p>" &&
                <div dangerouslySetInnerHTML={{ __html: activity?.materialsNeeded }} />}
              <h3 style={{ color: "#6630da", fontWeight: "bolder", marginTop: "20px" }}>YOU CAN EARN...!</h3>
              <StyledSkillNameSpan>
                {activity?.skill && activity?.skill.name && activity?.skill.name.toUpperCase()}
                <br />
                +{activity?.xps}XPs
              </StyledSkillNameSpan>
            </StyledOverlay>
          </Col>
          {activity?.instructionText || activity?.video || activity?.instructionImage ? <Col lg={{ span: 8, offset: 2 }} xs={{ span: 12, offset: 0 }} className="bg-white mt-5 rounded-lg">
            <StyledBox>
              <StyledBoxHeader>How To Do It</StyledBoxHeader>
              <img src="/assets/svg/ActivityDetailHowToDoItTag.svg" />
            </StyledBox>
            <StyledOverlay>
              {activity?.video && <ReactPlayer
                url={activity?.video}
                width="100%"
                config={{ vimeo: { playerOptions: { controls: true } } }}
              />}
              {activity?.instructionImage && <img src={activity?.instructionImage} style={{ maxWidth: "90%" }} />}
              {activity?.instructionText && activity?.instructionText !== "<p><br></p>" && <div dangerouslySetInnerHTML={{ __html: activity?.instructionText }} />}
            </StyledOverlay>
          </Col> : null}
          {activity?.description && activity?.description !== "<p><br></p>" && <Col lg={{ span: 8, offset: 2 }} xs={{ span: 12, offset: 0 }} className="bg-white mt-5 rounded-lg">
            <StyledBox>
              <StyledBoxHeader>Know More About It?</StyledBoxHeader>
              <img src="/assets/svg/ActivityDetailKnowMoreAboutIt.svg" />
            </StyledBox>
            <StyledOverlay>
              {activity?.description && activity?.description !== "<p><br></p>" &&
                <div dangerouslySetInnerHTML={{ __html: activity?.description }} />}
            </StyledOverlay>
          </Col>}
          <Col lg={{ span: 8, offset: 2 }} xs={{ offset: 0 }} className="bg-white mt-5 rounded-lg">
            <StyledBox>
              <StyledBoxHeader>What Have Others Done?</StyledBoxHeader>
              <img src="/assets/svg/ActivityDetailWhatOthersHaveDoneTag.svg" />
            </StyledBox>
            <StyledOverlay>
              <div style={{ display: "flex", gap: 6 }}>
                <img src="/assets/img/comments-placeholder.png" height={200} />
                <div style={{ margin: "auto" }}>
                  <h3 style={{ fontWeight: "bold" }}>Do you want to know what other students have done in the activity?</h3>
                  <Link href="/login" legacyBehavior><StyledButton>LOGIN NOW</StyledButton></Link>
                </div>
              </div>
            </StyledOverlay>
          </Col>
        </Row>
      </>
        : <Row>
          <Col lg={{ span: 8, offset: 2 }}>
            <Image src="/assets/img/not-found-placeholder.jpeg" height={400} width={400} alt="Activity Not Found" />
            <div style={{ fontSize: 20, fontWeight: 700 }}>The activity you&apos;re trying to access is not available for your school or standard. Please Contact us if you think you&apos;re looking on the right page.
            </div>
            <StyledButton onClick={() => router.push("/activities")}>
              Check Other Activities
            </StyledButton>
          </Col>
        </Row>}
    </Container>
  )
}
